import {
  applySnapshot,
  flow,
  Instance,
  SnapshotIn,
  types,
} from "mobx-state-tree";
import { DeleteEmailCycleRPC } from "@pulse/pulse-rpcs/lib/email-cycle/deleteEmailCycleRPC";
import { getAPIClient } from "../../networking/APIClient";
import { LeoUUID } from "@surya-digital/leo-ts-runtime";
import { LeoRPCResult } from "@surya-digital/leo-ts-types";
import { useDeleteEmailCycleRPCClientImpl } from "../rpcs/RPC";
import { EMPTY_CHARACTER } from "@pulse/shared-components";
import {
  BoundedNameModel,
  createBoundedNameModel,
} from "../../models/BoundedNameModel";

type DeleteEmailCycleScope = DeleteEmailCycleRPC.RequestEnums.Scope.Scope;

const getInitialState = (): SnapshotIn<typeof DeleteEmailCycleStore> => ({
  cycleId: EMPTY_CHARACTER,
  emailCycleName: createBoundedNameModel(),
  scope: DeleteEmailCycleRPC.RequestEnums.Scope.Scope.ONLY_THIS,
  deleteEmailCycleRPCError: null,
});

export const DeleteEmailCycleStore = types
  .model("DeleteEmailCycleStore", {
    cycleId: types.string,
    emailCycleName: BoundedNameModel,
    scope: types.enumeration(
      "DeletionScope",
      Object.values(DeleteEmailCycleRPC.RequestEnums.Scope.Scope),
    ),
    deleteEmailCycleRPCError: types.maybeNull(
      types.enumeration(
        "DeleteEmailCycleRPCError",
        Object.values(DeleteEmailCycleRPC.RPCError),
      ),
    ),
  })
  .actions((store) => ({
    setCycleId: (cycleId: string): void => {
      store.cycleId = cycleId;
    },
    setScope: (scope: DeleteEmailCycleScope): void => {
      store.scope = scope;
    },
    deleteEmailCycle: flow(function* () {
      store.deleteEmailCycleRPCError = null;

      const apiClient = getAPIClient(store);
      const request = new DeleteEmailCycleRPC.Request(
        new LeoUUID(store.cycleId),
        store.scope,
      );

      const result: LeoRPCResult<
        DeleteEmailCycleRPC.Response,
        DeleteEmailCycleRPC.Errors.Errors
      > = yield useDeleteEmailCycleRPCClientImpl(apiClient).execute(request);

      if ("response" in result) return;

      switch (result.error.code) {
        case DeleteEmailCycleRPC.RPCError.InvalidScheduleId:
        case DeleteEmailCycleRPC.RPCError.ScheduleInProgress:
          store.deleteEmailCycleRPCError = result.error.code;
          break;
        default:
          result.error.code satisfies never;
      }
    }),
    resetStore: (): void => applySnapshot(store, getInitialState()),
  }));

export const createDeleteEmailCycleStore = (): Instance<
  typeof DeleteEmailCycleStore
> => DeleteEmailCycleStore.create(getInitialState());
